import { useState } from "react";
import { Check, ExternalLink, KeyRound, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import { deleteAgentCredentials, saveAgentCredentials } from "../../lib/aiAgent";

/**
 * Configurazione guidata della chiave del coach (BYOK).
 * Spec: docs/ai-program/10-byok-and-mcp-rework.md (Fase 2, ADR-100).
 *
 * Unico punto in cui si configura l'assistente: lo usano sia la card in
 * Impostazioni sia il pannello di chat del Program Builder, cosi' il coach
 * non deve uscire da dove si trova per attivarlo.
 *
 * **Perche' una chiave API e non una "login con ChatGPT".** Oggi non esiste un
 * modo per un'app di terze parti di usare l'abbonamento ChatGPT o Claude di un
 * utente: gli abbonamenti e le API sono account e fatturazioni separate.
 * "Sign in with ChatGPT" esiste ma e' riservato a Plus/Pro e, per le app
 * commerciali, su lista d'attesa. La via che funziona oggi e' la chiave a
 * consumo — e il malinteso va disinnescato qui, non al primo errore.
 */

const PROVIDERS = {
	anthropic: {
		label: "Anthropic (Claude)",
		console: "https://console.anthropic.com/settings/keys",
		placeholder: "sk-ant-api03-...",
		steps: [
			"Apri console.anthropic.com e accedi",
			"Vai su Settings → API keys → Create key",
			"Carica qualche euro in Billing: le API sono a consumo, separate dall'abbonamento Claude",
		],
	},
	openai: {
		label: "OpenAI (GPT)",
		console: "https://platform.openai.com/api-keys",
		placeholder: "sk-proj-...",
		steps: [
			"Apri platform.openai.com e accedi con il tuo account",
			"Vai su API keys → Create new secret key e copiala subito (non si rivede)",
			"Carica qualche euro in Billing: l'abbonamento ChatGPT NON copre le API",
		],
	},
};

export function AIAgentSetup({ status, onChanged, compact = false }) {
	const [provider, setProvider] = useState(status?.provider || "anthropic");
	const [apiKey, setApiKey] = useState("");
	const [saving, setSaving] = useState(false);

	const info = PROVIDERS[provider];

	const handleSave = async () => {
		if (!apiKey.trim()) return;
		setSaving(true);
		try {
			const result = await saveAgentCredentials(provider, apiKey.trim());
			setApiKey("");
			toast.success(
				`Chiave verificata: ${result.models_available} modelli disponibili.`,
			);
			onChanged?.();
		} catch (error) {
			toast.error(error.message || "Impossibile salvare la chiave");
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async () => {
		try {
			await deleteAgentCredentials();
			toast.success("Chiave rimossa");
			onChanged?.();
		} catch (error) {
			toast.error(error.message || "Impossibile rimuovere la chiave");
		}
	};

	if (!status?.byok_available) {
		return (
			<div className="rounded-sm border border-blaze/40 bg-blaze/10 p-3 text-sm text-blaze">
				L'assistente non è attivo su questo server: manca un
				<code className="mx-1">JWT_SECRET_KEY</code> configurato, da cui
				derivare la cifratura delle chiavi.
			</div>
		);
	}

	return (
		<div className="space-y-4">
			{status.configured && (
				<div
					className="flex items-center justify-between gap-3 rounded-sm border border-volt/40 bg-volt/10 p-3"
					data-testid="ai-agent-configured"
				>
					<div className="flex items-center gap-2 text-sm">
						<Check className="h-4 w-4 shrink-0 text-volt" />
						<span>
							Chiave {status.provider} attiva{" "}
							<span className="text-muted-foreground">
								({status.api_key_hint})
							</span>
						</span>
					</div>
					<Button
						type="button"
						variant="ghost"
						size="sm"
						onClick={handleDelete}
						className="shrink-0 text-muted-foreground hover:text-blaze"
						aria-label="Rimuovi la chiave"
					>
						<Trash2 className="h-4 w-4" />
					</Button>
				</div>
			)}

			<div className="space-y-2">
				<Label>Motore AI</Label>
				<Select value={provider} onValueChange={setProvider}>
					<SelectTrigger className="rounded-sm">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						{Object.entries(PROVIDERS).map(([value, item]) => (
							<SelectItem key={value} value={value}>
								{item.label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>

			{!compact && (
				<ol className="list-decimal space-y-1 rounded-sm border border-border bg-secondary/40 p-3 pl-7 text-xs text-muted-foreground">
					{info.steps.map((step) => (
						<li key={step}>{step}</li>
					))}
				</ol>
			)}

			<div className="space-y-2">
				<Label htmlFor="ai-agent-key">
					{status.configured ? "Sostituisci la chiave" : "Incolla la chiave API"}
				</Label>
				<div className="flex gap-2">
					<Input
						id="ai-agent-key"
						type="password"
						autoComplete="off"
						placeholder={info.placeholder}
						value={apiKey}
						onChange={(event) => setApiKey(event.target.value)}
						onKeyDown={(event) => {
							if (event.key === "Enter") handleSave();
						}}
						className="rounded-sm font-mono"
					/>
					<Button
						type="button"
						onClick={handleSave}
						disabled={saving || !apiKey.trim()}
						className="shrink-0 rounded-sm"
					>
						{saving ? (
							<Loader2 className="mr-2 h-4 w-4 animate-spin" />
						) : (
							<KeyRound className="mr-2 h-4 w-4" />
						)}
						{saving ? "Verifico…" : "Attiva"}
					</Button>
				</div>

				<a
					href={info.console}
					target="_blank"
					rel="noreferrer"
					className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-volt"
				>
					Apri la console {info.label} <ExternalLink className="h-3 w-3" />
				</a>

				<p className="text-xs text-muted-foreground">
					La chiave viene provata subito e salvata cifrata; non torna più
					indietro. È <strong>tua</strong>: paghi i token al provider, nessun
					credito di piattaforma. Un abbonamento ChatGPT o Claude non abilita le
					API — serve una chiave a consumo dalla console.
				</p>
			</div>
		</div>
	);
}
